/**
 * AppShell - brand, primary navigation, session footer, main content area,
 * global toasts, confirm dialog, detail modal, and mobile nav. Every page
 * renders into `shell.outlet`.
 */
import { el } from "../utils/dom";
import { NAV_SECTIONS } from "./nav";
import { mountToastRegion } from "../components/toast";
import { mountConfirmHost } from "../components/confirmDialog";
import { mountModalHost } from "../components/modal";

export interface AppShellOptions {
  onNavigate: (path: string) => void;
  onSignOut: () => void;
  /** Display name from Entra when available. */
  signedInName?: string | null;
  /** Email from Entra when available. */
  signedInEmail?: string | null;
}

/** Temporary account footer until Entra identity is wired up. */
const FALLBACK_EMAIL = "anushka@cognida.ai";

export class AppShell {
  readonly outlet: HTMLElement;
  private navLinks: Array<{ path: string; el: HTMLElement }> = [];
  private rootEl: HTMLElement;

  constructor(container: HTMLElement, private options: AppShellOptions) {
    this.outlet = el("div", { className: "shell__content" });

    this.rootEl = this.buildRoot();
    container.replaceChildren();
    container.append(this.rootEl);

    mountToastRegion(this.rootEl);
    mountConfirmHost(this.rootEl);
    mountModalHost(this.rootEl);
  }

  setActiveRoute(path: string): void {
    for (const link of this.navLinks) {
      const active = this.isActive(path, link.path);
      if (active) link.el.setAttribute("aria-current", "page");
      else link.el.removeAttribute("aria-current");
    }
  }

  /**
   * A nav link is active when it matches the current path (exact or as a
   * parent prefix for nested detail routes). When multiple sidebar links
   * match - e.g. `/sync` and `/sync/history` for `#/sync/history` - only
   * the most specific (longest) path wins so siblings stay mutually exclusive.
   */
  private isActive(currentPath: string, routePath: string): boolean {
    if (routePath === "/dashboard" && currentPath === "/") return true;
    if (!this.pathMatches(currentPath, routePath)) return false;

    for (const link of this.navLinks) {
      if (link.path === routePath) continue;
      if (
        this.pathMatches(currentPath, link.path) &&
        link.path.length > routePath.length
      ) {
        return false;
      }
    }
    return true;
  }

  private pathMatches(currentPath: string, routePath: string): boolean {
    return currentPath === routePath || currentPath.startsWith(`${routePath}/`);
  }

  private navigate(path: string): void {
    this.closeMobileNav();
    this.options.onNavigate(path);
  }

  private closeMobileNav(): void {
    this.rootEl.classList.remove("shell--nav-open");
  }

  private buildRoot(): HTMLElement {
    const sections = NAV_SECTIONS.map((section) => {
      const items = section.items;
      if (!items.length) return null;
      return el("div", { className: "nav-section" }, [
        el("div", { className: "nav-section__label" }, [section.label]),
        ...items.map((item) => {
          const link = el(
            "div",
            {
              className: "nav-link",
              role: "link",
              tabIndex: 0,
              title: item.description,
              onclick: () =>
                item.external ? window.location.assign(item.path) : this.navigate(item.path),
              onkeydown: (ev: KeyboardEvent) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  if (item.external) window.location.assign(item.path);
                  else this.navigate(item.path);
                }
              },
            },
            [item.label]
          );
          this.navLinks.push({ path: item.path, el: link });
          return link;
        }),
      ]);
    }).filter((node): node is HTMLDivElement => node != null);

    const sidebar = el("div", { className: "shell__sidebar" }, [
      el("div", { className: "shell__brand" }, [
        el("span", { className: "shell__brand-name" }, ["Axis"]),
      ]),
      ...sections,
      this.renderFooter(),
    ]);

    const backdrop = el("div", {
      className: "shell__sidebar-backdrop",
      onclick: () => this.closeMobileNav(),
    });

    const topbar = el("div", { className: "shell__topbar" }, [
      el(
        "button",
        {
          className: "shell__menu-button",
          "aria-label": "Toggle navigation",
          onclick: () => this.rootEl.classList.toggle("shell--nav-open"),
        },
        ["\u2630"]
      ),
      el("span", { className: "shell__brand-name" }, ["Axis"]),
    ]);

    const main = el("div", { className: "shell__main" }, [topbar, this.outlet]);

    return el("div", { className: "shell" }, [backdrop, sidebar, main]);
  }

  /**
   * Account footer: Entra name + email when signed in; otherwise the
   * temporary fallback email until Entra is enabled.
   */
  private renderFooter(): HTMLElement {
    const name = this.options.signedInName?.trim() || null;
    const email = this.options.signedInEmail?.trim() || null;
    const identityChildren = [];

    if (name || email) {
      if (name) {
        identityChildren.push(
          el("span", { className: "shell__account-name", title: name }, [name])
        );
      }
      if (email) {
        identityChildren.push(
          el("span", { className: "shell__account-email", title: email }, [email])
        );
      }
    } else {
      identityChildren.push(
        el("span", { className: "shell__account-email", title: FALLBACK_EMAIL }, [FALLBACK_EMAIL])
      );
    }

    return el("div", { className: "shell__footer" }, [
      el(
        "a",
        {
          className: "shell__docs-link",
          href: "/documentation",
          title: "Open the consumer API reference",
          onclick: () => {
            // Admin console is dark-only; land the docs in the same theme.
            try {
              localStorage.setItem("apiDocsTheme", "dark");
            } catch {
              /* ignore */
            }
          },
        },
        ["API documentation"]
      ),
      el("div", { className: "shell__account" }, [
        el("div", { className: "shell__account-identity" }, identityChildren),
        el(
          "button",
          {
            className: "shell__sign-out",
            type: "button",
            onclick: () => this.options.onSignOut(),
          },
          ["Sign out"]
        ),
      ]),
    ]);
  }
}
